import React from 'react';
import { Step, StepButton, Stepper } from '@mui/material';

interface WizardStepIndicatorProps {
  /** Ordered list of step labels to render (5 for suppliers, 6 for admins). */
  steps: readonly string[];
  /** Zero-based index of the active step. */
  currentStep: number;
  /** Indices of completed steps (visited and passing validation), rendered with a checkmark. */
  completedSteps: number[];
  /** Jump to a step. The wizard auto-saves the current step before switching. */
  onStepClick: (index: number) => void;
  /** Disables interaction while a save is in flight (prevents duplicate navigation). */
  disabled?: boolean;
}

/**
 * Horizontal, fully clickable progress indicator for the Create/Edit Location
 * wizard. Every step is a focusable button so users can jump freely (forward or
 * backward) regardless of completeness; the wizard handles auto-saving the
 * current step before switching.
 *
 * Visual states:
 * - Visited (and not active) steps show a checkmark (✓) via MUI's `completed`.
 * - The active step is highlighted and marked with `aria-current="step"`.
 * - Remaining steps are inactive but still clickable.
 */
const WizardStepIndicator = (props: WizardStepIndicatorProps) => {
  const { steps, currentStep, completedSteps, onStepClick, disabled } = props;
  const completed = new Set(completedSteps);

  return (
    <Stepper
      nonLinear
      activeStep={currentStep}
      alternativeLabel
      aria-label="Location profile steps"
    >
      {steps.map((label, index) => {
        const isActive = index === currentStep;
        return (
          <Step key={label} completed={completed.has(index) && !isActive}>
            <StepButton
              onClick={() => { void onStepClick(index); }}
              disabled={disabled}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Step ${index + 1}: ${label}`}
              sx={{ minHeight: 48 }}
            >
              {label}
            </StepButton>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default WizardStepIndicator;

